import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Illustration,
  ListRow,
  ListRowGroup,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { AuditLogTimelineLoader } from '../../../shared/components';
import { AuditLogViewerError } from '../../../shared/components/AuditLogViewerError';
import { useInvertedLocalize } from '../../../shared/hooks';
import { auditLogActions } from '../data-store';

import TimelineItem from './TimelineItem';
// Column visibility is handled inside TimelineItem via appliedColumnFilters
import type { TimeLineBlockProps, TimelineData } from './types';

import './TimeLineBlock.css';

/**
 * TimeLineBlock Component
 *
 * Main timeline component that displays audit log events in a chronological timeline view.
 * Matches the exact design from Figma with vertical timeline layout, timestamps, and event details.
 */
function TimeLineBlock({
  className,
  timelineData = [],
  onItemClick,
  onTimestampClick,
  selectedItemId,
  isLoading = false,
  appliedColumnFilters,
  error = null,
  onRetry,
  onClearAllFilters,
  isDetailPanelOpen = false,
}: TimeLineBlockProps): React.ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  // Removed getGroupForLogType; relying on appliedColumnFilters only

  const handleItemClick = (
    item: TimelineData,
    tab?: 'summary' | 'review-activity'
  ) => {
    if (onItemClick) {
      onItemClick(item, tab);
    }
  };

  if (isLoading) {
    return (
      <Box className={cx('Aag-TimeLineBlock', className)}>
        <AuditLogTimelineLoader count={5} />
      </Box>
    );
  }

  // Show error state for any non-null/undefined error value
  if (error) {
    return (
      <Box className={cx('Aag-TimeLineBlock', className)}>
        <AuditLogViewerError onRetry={onRetry} />
      </Box>
    );
  }

  if (timelineData.length === 0) {
    return (
      <Box className={cx('Aag-TimeLineBlock', className)}>
        <Box className='Aag-TimeLineBlock-empty'>
          <Box className='Aag-TimeLineBlock-emptyContent'>
            <Illustration
              kind='Empty'
              className='Aag-TimeLineBlock-emptyIcon'
            />
            <Typography
              lineLimit={3}
              kind='h4'
              className='Aag-TimeLineBlock-emptyTitle'>
              {l('No log details found')}
            </Typography>
            <Typography
              lineLimit={3}
              kind='body2'
              className='Aag-TimeLineBlock-emptyDesc'>
              {l(
                'Try adjusting your filters or check back later. If the issue, persists, contact your administrator.'
              )}
            </Typography>
            {/* render a clear button here */}
            <Button
              kind='filled'
              size='small'
              onClick={() => {
                if (onClearAllFilters) {
                  onClearAllFilters();
                } else {
                  dispatch(auditLogActions.setFilters([]));
                }
              }}>
              {l('Clear Filters')}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={cx('Aag-TimeLineBlock', className)}>
      <ListRowGroup>
        {timelineData.map((item, index) => (
          <ListRow
            key={item.uniqueLogId}
            data-unique-id={String(item.uniqueLogId)}
            centerContent={
              <TimelineItem
                item={item}
                isSelected={selectedItemId === item.uniqueLogId}
                isLast={index === timelineData.length - 1}
                onClick={(tab?: 'summary' | 'review-activity') =>
                  handleItemClick(item, tab)
                }
                onTimestampClick={
                  onTimestampClick
                    ? () => {
                        onTimestampClick(item);
                      }
                    : undefined
                }
                appliedColumnFilters={appliedColumnFilters}
                isDetailPanelOpen={isDetailPanelOpen}
              />
            }
            isListItem={false}
            isCheckboxDisabled
            isRowDetailsOpen={selectedItemId === item.uniqueLogId}
            connectorDistance='34px'
          />
        ))}
      </ListRowGroup>
    </Box>
  );
}

TimeLineBlock.defaultProps = {
  className: undefined,
  timelineData: [],
  onItemClick: undefined,
  onTimestampClick: undefined,
  selectedItemId: undefined,
  isLoading: false,
  appliedColumnFilters: undefined,
  error: null,
  onRetry: undefined,
  onClearAllFilters: undefined,
  isDetailPanelOpen: false,
};

export default TimeLineBlock;
